import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from "@mui/material";
import { ArrowBack, History, Today } from "@mui/icons-material";
import api from "../../api/axios";

function formatDate(timestamp) {
  if (!timestamp) return "—";
  const d = new Date(timestamp);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function formatTime(timestamp) {
  if (!timestamp) return "—";
  const d = new Date(timestamp);
  return d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
}

const actionConfig = {
  PRODUCT_CREATED: { label: "Product Added", color: "success", icon: "➕" },
  PRODUCT_UPDATED: { label: "Product Updated", color: "info", icon: "✏️" },
  PRODUCT_DELETED: { label: "Product Deleted", color: "error", icon: "🗑️" },
  PRODUCT_ARCHIVED: { label: "Product Archived", color: "warning", icon: "📦" },
  PRODUCT_UNARCHIVED: { label: "Product Restored", color: "success", icon: "♻️" },
  PRODUCT_STATUS_CHANGED: { label: "Status Changed", color: "info", icon: "🔄" },
  PROFILE_UPDATED: { label: "Profile Updated", color: "primary", icon: "👤" },
  POLICIES_UPDATED: { label: "Policies Updated", color: "primary", icon: "📋" },
  STORE_SETTINGS_UPDATED: { label: "Store Settings Updated", color: "primary", icon: "⚙️" },
  LOGO_UPDATED: { label: "Logo Updated", color: "info", icon: "🖼️" },
  BANNER_UPDATED: { label: "Banner Updated", color: "info", icon: "🖼️" },
  STOREFRONT_IMAGES_UPDATED: { label: "Storefront Images Updated", color: "info", icon: "🏪" },
  SPONSORSHIP_APPLIED: { label: "Sponsorship Applied", color: "warning", icon: "⭐" },
  SPONSORSHIP_PAYMENT_COMPLETED: { label: "Sponsorship Activated", color: "success", icon: "✅" },
};

const CARD_ACCENTS = {
  total: { icon: "#0062ff", bg: "rgba(0,98,255,0.10)", border: "#dbeafe" },
  today: { icon: "#16a34a", bg: "rgba(22,163,74,0.10)", border: "#dcfce7" },
  productsAdded: { icon: "#f59e0b", bg: "rgba(245,158,11,0.10)", border: "#fef3c7" },
  productsUpdated: { icon: "#0062ff", bg: "rgba(0,98,255,0.10)", border: "#dbeafe" },
};

const CARD_ICONS = {
  total: <History sx={{ fontSize: { xs: 18, sm: 20, lg: 18 }, color: "#0062ff" }} />,
  today: <Today sx={{ fontSize: { xs: 18, sm: 20, lg: 18 }, color: "#16a34a" }} />,
  productsAdded: <span style={{ fontSize: 18 }}>➕</span>,
  productsUpdated: <span style={{ fontSize: 18 }}>✏️</span>,
};

const LIMIT = 20;

export default function MyActivity() {
  const navigate = useNavigate();

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [actionFilter, setActionFilter] = useState("");
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchPage(1);
  }, [actionFilter]);

  const fetchPage = async (pageNum) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const params = new URLSearchParams({ page: pageNum, limit: LIMIT });
      if (actionFilter) params.append("action", actionFilter);

      const { data } = await api.get(`/vendor/activities?${params}`);
      setActivities((prev) => pageNum === 1 ? data.activities : [...prev, ...data.activities]);
      setPage(pageNum);
      setTotalCount(data.pagination?.total || 0);
    } catch (err) {
      console.error("Failed to fetch activities:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await api.get("/vendor/activities/stats");
      setStats(data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  };

  const handleLoadMore = () => fetchPage(page + 1);
  const hasMore = activities.length < totalCount;

  const filteredActivities = useMemo(() => {
    if (!searchTerm.trim()) return activities;
    const term = searchTerm.toLowerCase();
    return activities.filter((a) => {
      const config = actionConfig[a.action] || { label: a.action };
      return (
        (a.description || "").toLowerCase().includes(term) ||
        config.label.toLowerCase().includes(term) ||
        (a.page || "").toLowerCase().includes(term)
      );
    });
  }, [activities, searchTerm]);

  const kpiCards = useMemo(() => [
    { id: "total", title: "Total Activities", value: stats?.totalActivities || 0 },
    { id: "today", title: "Today's Activities", value: stats?.todayActivities || 0 },
    { id: "productsAdded", title: "Products Added", value: stats?.byAction?.PRODUCT_CREATED || 0 },
    { id: "productsUpdated", title: "Products Updated", value: stats?.byAction?.PRODUCT_UPDATED || 0 },
  ], [stats]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f0f2f5" }}>
      {/* Header */}
      <Box sx={{ background: "linear-gradient(135deg, #0f172a 0%, #0062ff 100%)", px: { xs: 2, sm: 4 }, py: 2.5 }}>
        <Button onClick={() => navigate("/vendor/dashboard")} sx={{ color: "rgba(255,255,255,0.7)", textTransform: "none", fontSize: "0.82rem", fontWeight: 500, mb: 1, p: 0, "&:hover": { color: "#fff", bgcolor: "transparent" }, display: "flex", alignItems: "center", gap: 0.5 }}>
          <ArrowBack sx={{ fontSize: 16 }} />
          Back to Dashboard
        </Button>
        <Typography variant="h5" fontWeight={700} sx={{ color: "#fff", mb: 0.5 }}>📋 My Activity</Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>View all your store activities and actions</Typography>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 4 }, py: 3, maxWidth: 1200, mx: "auto" }}>
        {/* KPI Widgets */}
        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, alignItems: "stretch", mb: 3 }}>
          {kpiCards.map((c) => {
            const accent = CARD_ACCENTS[c.id] || CARD_ACCENTS.total;
            return (
              <Box key={c.id} sx={{ p: 1.25, borderRadius: "12px", bgcolor: "#fff", border: `1.5px solid ${accent.border}`, boxShadow: "0 2px 10px rgba(15,23,42,0.06)", display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.25 }, width: "100%", boxSizing: "border-box", minHeight: { xs: 80, sm: 80, md: 90 } }}>
                <Box sx={{ width: { xs: 34, sm: 38, lg: 36 }, height: { xs: 34, sm: 38, lg: 36 }, borderRadius: "10px", bgcolor: accent.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {CARD_ICONS[c.id] || CARD_ICONS.total}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem", lg: "0.68rem" }, fontWeight: 500, color: "#64748b", lineHeight: 1.2, mb: 0.4, overflow: "hidden", textOverflow: "ellipsis" }}>{c.title}</Typography>
                  <Typography sx={{ fontSize: { xs: "1.1rem", sm: "1.2rem", lg: "1rem" }, fontWeight: 700, lineHeight: 1, color: "#0f172a" }}>{c.value}</Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Activity List */}
        <Paper sx={{ borderRadius: 2, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
          {/* Header */}
          <Box sx={{ px: 2, py: 1.5, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#1e293b", display: "flex", alignItems: "center", gap: 1, mr: "auto" }}>
              <History sx={{ fontSize: 18, color: "#0062ff" }} />
              Activity Log ({totalCount} total)
            </Typography>
          </Box>

          {/* Filters row */}
          <Box sx={{ px: 2, pt: 1.5, pb: 1, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Filter by Action</InputLabel>
              <Select
                value={actionFilter}
                label="Filter by Action"
                onChange={(e) => { setActionFilter(e.target.value); }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
              >
                <MenuItem value="">All Actions</MenuItem>
                {Object.entries(actionConfig).map(([key, config]) => (
                  <MenuItem key={key} value={key}>
                    {config.icon} {config.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {actionFilter && (
              <Button size="small" variant="outlined" onClick={() => setActionFilter("")} sx={{ borderRadius: "8px", textTransform: "none", fontSize: "0.75rem" }}>
                Clear Filter
              </Button>
            )}
            <TextField
              size="small"
              placeholder="🔍 Search activities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ ml: "auto", minWidth: 200, "& input": { fontSize: "0.82rem" } }}
            />
          </Box>

          {/* Loading */}
          {loading ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <CircularProgress size={32} sx={{ color: "#0062ff", mb: 1.5 }} />
              <Typography variant="body2" sx={{ color: "#94a3b8" }}>Loading activities...</Typography>
            </Box>
          ) : filteredActivities.length === 0 ? (
            <Box sx={{ p: 6, textAlign: "center" }}>
              <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: "rgba(0,98,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
                <History sx={{ fontSize: 32, color: "#0062ff" }} />
              </Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#1e293b", mb: 0.5 }}>No activities found</Typography>
              <Typography variant="body2" sx={{ color: "#94a3b8" }}>Your store activities will appear here</Typography>
            </Box>
          ) : (
            <>
              {/* Activity items */}
              <Box sx={{ px: 2, py: 1.5 }}>
                {filteredActivities.map((activity, idx) => {
                  const config = actionConfig[activity.action] || { label: activity.action, color: "default", icon: "📌" };
                  const chipColor = config.color === "success" ? "success" : config.color === "error" ? "error" : config.color === "warning" ? "warning" : config.color === "info" ? "info" : "default";
                  const hasChanges = activity.changes && activity.changes.length > 0;

                  return (
                    <Paper
                      key={activity._id || idx}
                      sx={{
                        mb: 1.5,
                        p: 1.5,
                        borderRadius: "10px",
                        bgcolor: "#fff",
                        border: "1px solid #f1f5f9",
                        transition: "0.2s",
                        "&:hover": { borderColor: "#cbd5e1", bgcolor: "#fafafa" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                        {/* Icon circle */}
                        <Box sx={{ width: 36, height: 36, borderRadius: "50%", bgcolor: config.color === "success" ? "#e8f5e9" : config.color === "info" ? "#e3f2fd" : config.color === "warning" ? "#fff3e0" : config.color === "error" ? "#ffebee" : "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <Typography sx={{ fontSize: 16, lineHeight: 1 }}>{config.icon}</Typography>
                        </Box>

                        {/* Content */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.3, flexWrap: "wrap" }}>
                            <Typography variant="body2" fontWeight={600} sx={{ color: "#1e293b", fontSize: "0.82rem" }}>
                              {activity.description}
                            </Typography>
                            <Chip label={config.label} size="small" color={chipColor} sx={{ height: 18, fontSize: "0.6rem", fontWeight: 600 }} />
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                            <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.68rem", display: "flex", alignItems: "center", gap: 0.3 }}>
                              📅 {formatDate(activity.createdAt)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.68rem", display: "flex", alignItems: "center", gap: 0.3 }}>
                              ⏰ {formatTime(activity.createdAt)}
                            </Typography>
                            {activity.page && (
                              <Chip label={activity.page} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.6rem", color: "#64748b", borderColor: "#e2e8f0" }} />
                            )}
                          </Box>
                          {hasChanges && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" sx={{ color: "#64748b", fontStyle: "italic", fontSize: "0.7rem" }}>
                                {activity.changes.length} change{activity.changes.length > 1 ? "s" : ""} made
                              </Typography>
                              <Box sx={{ mt: 0.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
                                {activity.changes.map((change, ci) => (
                                  <Box key={ci} sx={{ display: "flex", alignItems: "center", gap: 0.5, p: 0.5, bgcolor: "#f8fafc", borderRadius: 1, flexWrap: "wrap" }}>
                                    <Typography variant="caption" fontWeight={600} sx={{ color: "#475569", fontSize: "0.68rem", minWidth: 90 }}>{change.label}:</Typography>
                                    {change.oldValue !== undefined && change.oldValue !== null && (
                                      <>
                                        <Typography variant="caption" sx={{ color: "#dc2626", textDecoration: "line-through", bgcolor: "#fef2f2", px: 0.5, py: 0.1, borderRadius: 0.5, fontSize: "0.68rem" }}>
                                          {String(change.oldValue).substring(0, 40)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.65rem" }}>→</Typography>
                                      </>
                                    )}
                                    <Typography variant="caption" sx={{ color: "#16a34a", bgcolor: "#f0fdf4", px: 0.5, py: 0.1, borderRadius: 0.5, fontSize: "0.68rem" }}>
                                      {String(change.newValue).substring(0, 40)}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>

              {/* Load More */}
              {hasMore && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2, pb: 3 }}>
                  <Button variant="outlined" onClick={handleLoadMore} disabled={loadingMore} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, px: 4 }}>
                    {loadingMore ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                    {loadingMore ? "Loading..." : "Load More"}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
}